import { DateTime } from "luxon"
import { createContext, PropsWithChildren, useCallback, useContext, useMemo } from "react"
import { useIsFetching } from "@tanstack/react-query"
import { useField } from "formik"
import { useSnackbar } from "notistack"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { DeliveryTimesField, DynamicConfigKey } from "../../form/formTypes"
import {
    ConfigQuery,
    fetchFreeBusyScheduleForResources,
    FreeBusyScheduleForResourcesResponse,
} from "../../../createOrderApi"
import { ResourceDetailsDialogParams } from "../../../../dialogs/resourceDetailsDialog/ResourceDetailsDialog"
import { isInPast, isOngoing } from "../calendar/helpers"
import { useTimelineContext } from "./TimelineContextProvider"
import { useResourceDetails } from "./ResourceDetailsProvider"
import { useResourceBookingDraft } from "../ResourceBookingDraftContext"
import { useResourceHelpers } from "../resourceBookingCalendars/timelineHooks"
import {
    createNewEvent,
    DEFAULT_DURATION_MINUTES,
    DEFAULT_MINIMUM_DURATION_MINUTES,
    earliestTime,
    roundDateTimeToNearestMinutes,
} from "../resourceBookingCalendars/helpers"

const transformFreeBusyScheduleForResources = (
    { resources }: FreeBusyScheduleForResourcesResponse,
    resourceId: string,
) => {
    if (!resources) return false

    const resource = resources.find((r) => r.resource.id === +resourceId) || resources[0]

    if (!resource) return false

    const availability = resource.availabilities.find((slot) => slot.availabilityView)

    if (!availability) return false

    return [...availability.availabilityView].every((c) => c === "0")
}

function yieldToBrowserForSpinnerPaint(): Promise<void> {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => resolve())
        })
    })
}

type ResourceLabelSharedContextValue = Readonly<{
    isLoadingEvents: boolean
    isLocked: boolean
    selectedResourceIdsSet: ReadonlySet<string>
    eventsLoadingNativeTitle: string
    searchRegex: RegExp | undefined
    toggleResourceSelection: (resourceId: string) => Promise<void>
    handleOpenDetails: (res: ResourceDetailsDialogParams) => void
}>

const ResourceLabelSharedContext = createContext<ResourceLabelSharedContextValue | null>(null)

type Props = PropsWithChildren<{}>

export function ResourceLabelSharedProvider({ children }: Props) {
    const [{ value: selectedDeliveries }] = useField<DeliveryTimesField | undefined>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )

    const { mutateAsync: callFreeBusyScheduleForResources } = useApiMutation(fetchFreeBusyScheduleForResources, {})
    const { enqueueSnackbar } = useSnackbar()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const eventsLoadingNativeTitle = l10n.getString("create-order-resource-row-events-loading-tooltip")

    const { openResourceDetailsDialog } = useResourceDetails()
    const { userId, localEventUpdateId, isBookPeriodMode, isOutlookFlow, searchRegex } = useTimelineContext()

    const activeFetches = useIsFetching({
        queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY, userId],
    })
    const isLoadingEvents = activeFetches > 0

    const { draftEvent: localEvent } = useResourceBookingDraft()
    const { handleChangeLocalEvent, isResourceAvailable } = useResourceHelpers({})

    const selectedResourceIdsSet = useMemo(
        () => new Set(localEvent?.resourceIds ?? []),
        [localEvent],
    )

    const isLocked = !!(localEventUpdateId && localEvent && isOngoing(localEvent, dateTimeFactory))

    const toggleResourceSelection = useCallback(
        async (resourceId: string) => {
            if (isLoadingEvents) return

            await yieldToBrowserForSpinnerPaint()

            if (isBookPeriodMode && localEvent) {
                if (!localEvent.resourceIds.includes(resourceId)) {
                    try {
                        const result = callFreeBusyScheduleForResources({
                            userId,
                            availabilityViewInterval: 5,
                            startTime: localEvent.start,
                            endTime: localEvent.end,
                            resourceIds: resourceId,
                        })

                        const isResourceFree = transformFreeBusyScheduleForResources(await result, resourceId)

                        if (isResourceFree) {
                            handleChangeLocalEvent({
                                ...localEvent,
                                resourceIds: [...localEvent.resourceIds, resourceId],
                            })
                        } else {
                            enqueueSnackbar({
                                variant: "warning",
                                message: l10n.getString("create-order-book-period-busy-message"),
                                autoHideDuration: 1500,
                            })
                        }
                        return
                    } catch (error) {
                        enqueueSnackbar({
                            variant: "error",
                            message: l10n.getString("create-order-book-period-busy-error"),
                        })
                        return
                    }
                } else {
                    const updatedResourceIds = localEvent.resourceIds.filter((id) => id !== resourceId)

                    handleChangeLocalEvent({
                        ...localEvent,
                        resourceIds: updatedResourceIds,
                    })

                    return
                }
            } else {
                if (localEvent) {
                    const hasOutlookTimeGuard = isOutlookFlow && localEvent && localEvent?.resourceIds.length === 0
                    if (hasOutlookTimeGuard) {
                        if (isInPast(localEvent.start) || isInPast(localEvent.end)) {
                            enqueueSnackbar({
                                variant: "warning",
                                message: l10n.getString("create-order-past-booking-attempt-message"),
                            })
                            return
                        }
                    }

                    if (localEvent.resourceIds.includes(resourceId)) {
                        const updatedResourceIds = localEvent.resourceIds.filter((id) => id !== resourceId)

                        if (updatedResourceIds.length === 0) {
                            if (isOutlookFlow) {
                                handleChangeLocalEvent({ ...localEvent, resourceIds: [] })
                            } else {
                                handleChangeLocalEvent(null)
                            }
                        } else {
                            handleChangeLocalEvent({
                                ...localEvent,
                                resourceIds: updatedResourceIds,
                            })
                        }
                        return
                    }

                    if (!isResourceAvailable(localEvent.start, localEvent.end, [resourceId], localEvent.id)) {
                        enqueueSnackbar({
                            variant: "warning",
                            message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
                        })
                        return
                    }

                    handleChangeLocalEvent({
                        ...localEvent,
                        resourceIds: [...localEvent.resourceIds, resourceId],
                    })
                } else {
                    let startDateTime: DateTime

                    if (
                        selectedDeliveries &&
                        selectedDeliveries.deliveryDate &&
                        selectedDeliveries.deliveryTimes?.length > 0
                    ) {
                        const deliveryDate = selectedDeliveries.deliveryDate
                        const time = earliestTime(selectedDeliveries.deliveryTimes)

                        startDateTime = dateTimeFactory.fromISO(`${deliveryDate}T${time}`)
                    } else {
                        startDateTime = roundDateTimeToNearestMinutes(
                            dateTimeFactory.now(),
                            DEFAULT_MINIMUM_DURATION_MINUTES,
                        )
                    }

                    const start = dateTimeFactory.toApiISOMinutesFormat(startDateTime)
                    const end = dateTimeFactory.toApiISOMinutesFormat(
                        startDateTime.plus({ minutes: DEFAULT_DURATION_MINUTES }),
                    )

                    if (!isResourceAvailable(start, end, [resourceId])) {
                        enqueueSnackbar({
                            variant: "warning",
                            message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
                        })
                        return
                    }

                    handleChangeLocalEvent(createNewEvent(resourceId, start, end))
                }
            }
        },
        [
            userId,
            isLoadingEvents,
            isBookPeriodMode,
            isOutlookFlow,
            localEvent,
            l10n,
            callFreeBusyScheduleForResources,
            selectedDeliveries,
            dateTimeFactory,
            handleChangeLocalEvent,
            enqueueSnackbar,
            isResourceAvailable,
        ],
    )

    const value = useMemo(
        () => ({
            isLoadingEvents,
            isLocked,
            selectedResourceIdsSet,
            eventsLoadingNativeTitle,
            searchRegex,
            toggleResourceSelection,
            handleOpenDetails: openResourceDetailsDialog,
        }),
        [
            isLoadingEvents,
            isLocked,
            selectedResourceIdsSet,
            eventsLoadingNativeTitle,
            searchRegex,
            toggleResourceSelection,
            openResourceDetailsDialog,
        ],
    )

    return <ResourceLabelSharedContext.Provider value={value}>{children}</ResourceLabelSharedContext.Provider>
}

export function useResourceLabelSharedContext() {
    const context = useContext(ResourceLabelSharedContext)

    if (!context) {
        throw new Error(
            "useResourceLabelSharedContext must be used within a ResourceLabelSharedProvider. " +
                "Check that you have wrapped your component in <ResourceLabelSharedProvider>.",
        )
    }

    return context
}
